'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { z } from 'zod'

export interface UseFilterOptions {
  defaultParams?: Partial<PublicFilterParams>
}

// Define the schema first
export const publicFilterSchema = z.object({
  page: z.number().default(1),
  take: z.number().default(12),
  search: z.string().optional(),
  sortBy: z.string().optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
  category: z.string().optional().default('all'),
  tag: z.union([z.string(), z.array(z.string())]).optional(),
  author: z.string().optional(),
  availability: z.string().optional(),
  vehicleType: z.string().optional(),
  // Additional filter fields needed by product pages
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  priceRange: z.string().optional(),
  rating: z.string().optional(),
  inStock: z.union([z.boolean(), z.enum(['true', 'false'])]).optional(),
  onSale: z.union([z.boolean(), z.enum(['true', 'false'])]).optional(),
  featured: z.union([z.boolean(), z.enum(['true', 'false'])]).optional(),
})

// Then infer the type from the schema
export type PublicFilterParams = z.infer<typeof publicFilterSchema>

export function usePublicFilter({
  defaultParams = {}
}: UseFilterOptions = {}) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)

  // Parse initial params from URL or use defaults
  const parseInitialParams = useCallback(() => {
    const initial = {
      page: Number(searchParams.get('page')) || defaultParams.page || 1,
      take: Number(searchParams.get('take')) || defaultParams.take || 12,
      search: searchParams.get('search') || defaultParams.search || '',
      sortBy: searchParams.get('sortBy') || defaultParams.sortBy || 'createdAt',
      order: (searchParams.get('order') as 'asc' | 'desc') || defaultParams.order || 'desc',
      category: searchParams.get('category') || defaultParams.category || 'all',
      tag: searchParams.get('tag') || defaultParams.tag,
      author: searchParams.get('author') || defaultParams.author,
      availability: searchParams.get('availability') || defaultParams.availability,
      vehicleType: searchParams.get('vehicleType') || defaultParams.vehicleType,
      dateFrom: searchParams.get('dateFrom') || defaultParams.dateFrom,
      dateTo: searchParams.get('dateTo') || defaultParams.dateTo,
      priceRange: searchParams.get('priceRange') || defaultParams.priceRange,
      rating: searchParams.get('rating') || defaultParams.rating,
      inStock: searchParams.get('inStock') === 'true' || searchParams.get('inStock') === 'false' || defaultParams.inStock,
      onSale: searchParams.get('onSale') === 'true' || searchParams.get('onSale') === 'false' || defaultParams.onSale,
      featured: searchParams.get('featured') === 'true' || searchParams.get('featured') === 'false' || defaultParams.featured,
    }

    return publicFilterSchema.parse(initial)
  }, [searchParams, defaultParams])

  const [params, setParams] = useState<PublicFilterParams>(parseInitialParams)

  // Update URL when params change
  const updateUrl = useCallback(async (newParams: Partial<PublicFilterParams>) => {
    setIsLoading(true);
    try {
      // Use functional update to get latest params
      setParams(currentParams => {
        // Create a new params object, handling undefined values as removals
        const updatedParams = { ...currentParams } as Record<string, any>;
        Object.entries(newParams).forEach(([key, value]) => {
          if (value === undefined || value === '') {
            delete updatedParams[key];
          } else {
            // Convert value to string for URL parameters
            updatedParams[key] = String(value);
          }
        });

        // Handle special case for sort options with direction
        if (typeof updatedParams.sortBy === 'string' && updatedParams.sortBy.includes('_')) {
          const [field, direction] = updatedParams.sortBy.split('_');
          updatedParams.sortBy = field;
          if (direction === 'asc' || direction === 'desc') {
            updatedParams.order = direction;
          }
        }

        // Check if params have actually changed
        const hasChanged = Object.entries(newParams).some(([key, value]) => {
          return currentParams[key as keyof PublicFilterParams] !== value;
        });

        if (!hasChanged) {
          return currentParams;
        }

        // Update URL
        const urlParams = new URLSearchParams();
        Object.entries(updatedParams).forEach(([key, value]) => {
          if (value !== undefined && value !== '' && value !== defaultParams[key as keyof PublicFilterParams]) {
            // Ensure value is converted to string properly
            urlParams.set(key, String(value));
          }
        });

        // Get the new URL string and update
        const newUrl = urlParams.toString() ? `${pathname}?${urlParams.toString()}` : pathname;
        router.replace(newUrl, { scroll: false });

        return updatedParams as PublicFilterParams;
      });
    } finally {
      setIsLoading(false);
    }
  }, [router, pathname, defaultParams]);

  // Listen for URL changes from back/forward navigation
  useEffect(() => {
    const newParams = parseInitialParams();
    const paramsStr = JSON.stringify(params);
    const newParamsStr = JSON.stringify(newParams);

    if (paramsStr !== newParamsStr) {
      setParams(newParams);
    }
  }, [searchParams, parseInitialParams]); // Keep params out of dependencies

  const clearFilters = useCallback(async () => {
    setIsLoading(true)
    try {
      // Reset to default values
      const defaultValues = {
        page: 1,
        take: defaultParams.take || 12,
        search: '',
        sortBy: defaultParams.sortBy || 'createdAt',
        order: defaultParams.order || 'desc',
        category: 'all',
        tag: undefined,
        author: undefined,
        availability: undefined,
        vehicleType: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        priceRange: undefined,
        rating: undefined,
        inStock: undefined,
        onSale: undefined,
        featured: undefined,
      }

      // Clear the URL by replacing with just the pathname
      await router.replace(pathname, { scroll: false })
      setParams(defaultValues)
    } finally {
      setIsLoading(false)
    }
  }, [router, pathname, defaultParams])

  return {
    params,
    isLoading,
    updateParams: updateUrl,
    // Convenience methods
    setPage: (page: number) => updateUrl({ page }),
    setPageSize: (take: number) => updateUrl({ take }),
    setSearch: (search: string) => updateUrl({ search, page: 1 }),
    setSort: (sortBy: PublicFilterParams['sortBy'], order?: 'asc' | 'desc') =>
      updateUrl({ sortBy, order, page: 1 }),
    setCategory: (category: string) => updateUrl({ category, page: 1 }),
    setTag: (tag: string) => updateUrl({ tag, page: 1 }),
    setAuthor: (author: string) => updateUrl({ author, page: 1 }),
    clearFilters
  }
}
